import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/auth/useAuth'
import { api } from '@/lib/api'

const tk = (tenantId: string, ...parts: unknown[]) => ['tenant', tenantId, 'tax', ...parts]

function makeQuery<T>(path: string) {
  return () => {
    const { user } = useAuth()
    const tenantId = user?.tenantId
    return useQuery({
      queryKey: tk(tenantId ?? 'anon', path),
      queryFn: () => api.get<{ data: T[] }>(path),
      enabled: Boolean(tenantId),
      staleTime: 0,
      refetchOnMount: 'always',
    })
  }
}

export interface TaxDashboardTiles {
  open_filings: number
  next_due: {
    id: string; filing_type: string; period_start: string; period_end: string
    due_date: string; status: string; due: string
  } | null
  overdue_count: number
  ytd_liability_minor_units: string
  active_registrations: number
}

export function useTaxDashboardTiles() {
  const { user } = useAuth()
  const tenantId = user?.tenantId
  return useQuery({
    queryKey: tk(tenantId ?? 'anon', 'dashboard'),
    queryFn: () => api.get<{ data: TaxDashboardTiles }>('/api/tax/dashboard/tiles'),
    enabled: Boolean(tenantId),
    staleTime: 30_000,
  })
}

export interface TaxFiling {
  id: string; filing_type: string; jurisdiction_id: string | null; tax_year: number
  period_start: string; period_end: string; due_date: string; status: string
  total_due_minor_units: string; total_paid_minor_units: string; currency: string
  signed_at: string | null; filed_at: string | null; acknowledged_at: string | null
  created_at: string; updated_at: string
}

export function useFilings(opts: { status?: string; filing_type?: string; filing_type_prefix?: string; tax_year?: number } = {}) {
  const { user } = useAuth()
  const tenantId = user?.tenantId
  const search = new URLSearchParams()
  if (opts.status) search.set('status', opts.status)
  if (opts.filing_type) search.set('filing_type', opts.filing_type)
  if (opts.filing_type_prefix) search.set('filing_type_prefix', opts.filing_type_prefix)
  if (opts.tax_year) search.set('tax_year', String(opts.tax_year))
  const suffix = search.toString() ? `?${search}` : ''
  return useQuery({
    queryKey: tk(tenantId ?? 'anon', 'filings', opts),
    queryFn: () => api.get<{ data: TaxFiling[] }>(`/api/tax/filings${suffix}`),
    enabled: Boolean(tenantId),
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export const useTaxPayments = makeQuery<{
  id: string; payment_kind: string; method: string
  amount_minor_units: string; currency: string
  scheduled_for: string; released_at: string | null; settled_at: string | null
  confirmation: string | null; status: string
  filing_type: string | null; tax_year: number | null
}>('/api/tax/payments')

export const useTaxCalendar = makeQuery<{
  id: string; filing_type: string; period_start: string; period_end: string
  due_date: string; status: string; reminder_sent_at: string | null
  filing_id: string | null
  jurisdiction_code: string | null; jurisdiction_name: string | null; jurisdiction_kind: string | null
}>('/api/tax/calendar')

export const useTaxJurisdictions = (opts: { kind?: string; country?: string; q?: string } = {}) => {
  const { user } = useAuth()
  const tenantId = user?.tenantId
  const search = new URLSearchParams()
  if (opts.kind) search.set('kind', opts.kind)
  if (opts.country) search.set('country', opts.country)
  if (opts.q) search.set('q', opts.q)
  const suffix = search.toString() ? `?${search}` : ''
  return useQuery({
    queryKey: tk(tenantId ?? 'anon', 'jurisdictions', opts),
    queryFn: () => api.get<{ data: Array<{
      id: string; code: string; kind: string; country_code: string; name: string
      short_name: string | null; rounding_mode: string; money_decimals: number; is_active: boolean
    }>}>(`/api/tax/jurisdictions${suffix}`),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  })
}

export const useTaxRegistrations = makeQuery<{
  id: string; purpose: string; account_number_last4: string | null; filing_frequency: string | null
  sui_rate: string | null; registration_date: string | null; deregistration_date: string | null
  status: string; jurisdiction_code: string; jurisdiction_name: string; jurisdiction_kind: string
}>('/api/tax/registrations')

export const useNexusObservations = makeQuery<{
  id: string; observation_date: string
  rolling_revenue_minor_units: string; rolling_transactions: number
  threshold_crossed: boolean; threshold_crossed_at: string | null; notified_at: string | null
  jurisdiction_code: string; jurisdiction_name: string
}>('/api/tax/nexus')

export const useExemptionCerts = makeQuery<{
  id: string; customer_id: number | null; certificate_number: string | null
  reason_kind: string | null; s3_key: string | null
  effective_from: string; effective_to: string | null
  jurisdiction_code: string; jurisdiction_name: string
}>('/api/tax/exemption-certificates')

export const useReconciliations = makeQuery<{
  id: string; reconciliation_kind: string; period_start: string; period_end: string
  ledger_total_minor_units: string; filing_total_minor_units: string
  currency: string; variance_minor_units: string
  findings: unknown[]; created_at: string
}>('/api/tax/reconciliations')

export const useTaxDocuments = makeQuery<{
  id: string; kind: string; file_name: string; s3_key: string
  mime_type: string | null; uploaded_at: string
  filing_type: string; tax_year: number; period_start: string; period_end: string
}>('/api/tax/documents')

export const useTaxpayers = makeQuery<{
  id: string; taxpayer_kind: string; legal_name: string; display_name: string | null
  tax_id_last4: string | null; tax_id_type: string
  country: string; state_region: string | null; currency: string; is_primary: boolean
  created_at: string; updated_at: string
}>('/api/tax/taxpayers')

export const useAIRecommendations = (feature: 'tax' | 'payroll' = 'tax') => {
  const { user } = useAuth()
  const tenantId = user?.tenantId
  return useQuery({
    queryKey: tk(tenantId ?? 'anon', 'ai-recs', feature),
    queryFn: () => api.get<{ data: Array<{
      id: string; feature: string; surface: string; model_version: string
      output: Record<string, unknown>; confidence: string | number
      citations: Array<{ table: string; id: string | number; field: string; value: unknown }>
      disclaimer_id: string | null; tokens_input: number; tokens_output: number; created_at: string
    }>}>(`/api/tax/ai/recommendations?feature=${feature}`),
    enabled: Boolean(tenantId),
  })
}

export const useDisclaimer = (slug: string) => {
  const { user } = useAuth()
  const tenantId = user?.tenantId
  return useQuery({
    queryKey: tk(tenantId ?? 'anon', 'disclaimer', slug),
    queryFn: () => api.get<{ data: { id: string; slug: string; version: number; locale: string; title: string; body_markdown: string } | null }>(
      `/api/tax/disclaimers/${encodeURIComponent(slug)}`,
    ),
    enabled: Boolean(tenantId) && Boolean(slug),
    staleTime: 60_000,
  })
}

export interface TaxEstimate {
  period: { tax_year: number; quarter: number | null; start: string; end: string }
  business: {
    company_type: string | null; country: string; state: string | null
    business_income_rate_assumed: number
    revenue_minor_units: string; cogs_minor_units: string; gross_profit_minor_units: string
    operating_expenses_minor_units: string; other_expense_minor_units: string
    net_income_minor_units: string
    estimated_federal_income_tax_minor_units: string
    estimated_quarterly_payment_minor_units: string
  }
  sales_tax: {
    total_collected_minor_units: string
    total_taxable_revenue_minor_units: string
    by_jurisdiction: Array<{
      jurisdiction: string; invoice_count: number
      taxable_revenue_minor_units: string; sales_tax_collected_minor_units: string
    }>
  }
  contractor_1099_candidates: {
    threshold_minor_units: number; threshold_currency: string; candidate_count: number
    candidates: Array<{
      vendor_id: number; vendor_name: string; vendor_state: string
      bill_count: number; total_paid_minor_units: string; reportable_on_1099: boolean
    }>
  }
  payroll: {
    posted_runs: number
    gross_minor_units: string; employer_tax_minor_units: string; employee_tax_minor_units: string
  }
  filings: { open: number; overdue: number }
  notes: string[]
}

export function useTaxEstimate(opts: { tax_year: number; quarter?: number | null }) {
  const { user } = useAuth()
  const tenantId = user?.tenantId
  const search = new URLSearchParams()
  search.set('tax_year', String(opts.tax_year))
  if (opts.quarter) search.set('quarter', String(opts.quarter))
  return useQuery({
    queryKey: tk(tenantId ?? 'anon', 'estimate', opts.tax_year, opts.quarter ?? 'annual'),
    queryFn: () => api.get<{ data: TaxEstimate }>(`/api/tax/estimate?${search}`),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  })
}

export interface RateLookupResult {
  query: { zip: string; state: string | null; product_class: string; as_of: string }
  matched_jurisdictions: Array<{
    id: string; code: string; name: string; rounding_mode: string; money_decimals: number
  }>
  active_rule_packs: Array<{ pack_key: string; pack_version: number; effective_from: string; payload: unknown }>
  note: string
}

export async function rateLookup(opts: { zip?: string; state?: string; product_class?: string; as_of?: string }) {
  const search = new URLSearchParams()
  if (opts.zip) search.set('zip', opts.zip)
  if (opts.state) search.set('state', opts.state)
  if (opts.product_class) search.set('product_class', opts.product_class)
  if (opts.as_of) search.set('as_of', opts.as_of)
  return api.get<{ data: RateLookupResult }>(`/api/tax/rates/lookup?${search}`)
}

export const useTaxReportCatalog = makeQuery<{
  slug: string; name: string; description: string; endpoint: string; required_params: string[]
}>('/api/tax/reports')

export async function askTaxAssistant(question: string) {
  return api.post<{ data: { id: string; output: { question: string; answer: string }; confidence: string | number; created_at: string } }>(
    '/api/tax/ai/ask',
    { question },
  )
}

export async function generate941(body: { tax_year: number; quarter: number }) {
  return api.post<{ data: TaxFiling }>('/api/tax/filings/generate-941', body)
}
export async function generate940(body: { tax_year: number }) {
  return api.post<{ data: TaxFiling }>('/api/tax/filings/generate-940', body)
}
export async function generateW2Batch(body: { tax_year: number }) {
  return api.post<{ data: { tax_year: number; w2_count: number } }>('/api/tax/filings/generate-w2-batch', body)
}
export async function generate1099Batch(body: { tax_year: number }) {
  return api.post<{ data: { tax_year: number; contractor_filings_created: number } }>('/api/tax/filings/generate-1099-batch', body)
}

export interface TaxpayerInput {
  taxpayer_kind?: string; legal_name: string; display_name?: string | null
  tax_id_last4?: string | null; tax_id_type?: string; country?: string
  address_line1?: string | null; city?: string | null; state_region?: string | null; postal_code?: string | null
  currency?: string; is_primary?: boolean; expected_updated_at?: string
}
export async function createTaxpayer(body: TaxpayerInput) {
  return api.post<{ data: { id: string } }>('/api/tax/taxpayers', body)
}
export async function updateTaxpayer(id: string, body: TaxpayerInput) {
  return api.put<{ data: { id: string } }>(`/api/tax/taxpayers/${id}`, body)
}

export async function createRegistration(body: {
  jurisdiction_id: string; purpose: string; account_number_last4?: string
  filing_frequency?: string; sui_rate?: number; status?: string
}) {
  return api.post<{ data: { id: string } }>('/api/tax/registrations', body)
}

export async function schedulePayment(body: {
  filing_id?: string; payment_kind?: string; method?: string
  amount_minor_units?: number; scheduled_for?: string
}) {
  return api.post<{ data: { id: string } }>('/api/tax/payments', body)
}
export async function releasePayment(id: string) {
  return api.post<{ data: { id: string; status: string; confirmation: string } }>(`/api/tax/payments/${id}/release`, {})
}

export async function runReconciliation(tax_year: number) {
  return api.post<{ data: unknown }>('/api/tax/reconciliations/run', { tax_year })
}

export async function generateCalendar(tax_year: number) {
  return api.post<{ data: unknown }>('/api/tax/calendar/generate', { tax_year })
}

export async function createExemptionCert(body: {
  jurisdiction_id: string; certificate_number: string; customer_id?: number
  reason_kind?: string; effective_from?: string; effective_to?: string
}) {
  return api.post<{ data: { id: string } }>('/api/tax/exemption-certificates', body)
}
